/*
  PANDRUM — panier latéral (19/10/2026), remplace l'appli Upcart. Chargé par sections/cart-drawer.liquid sur toutes les pages.
  1. Ajout au panier des fiches « lowdesign » (le formulaire n'a pas de script du thème : c'était Kaching/Upcart qui le prenaient en charge).
     Sur une fiche handpan, ajoute aussi les 7 cadeaux de « Votre offre du jour » (pandrum-offre.js).
  2. Garde les cadeaux alignés sur leur handpan : handpan retiré = cadeaux retirés ; 2 handpans = 2 lots de cadeaux.
  3. Ouvre / ferme : masque le chat Tidio et prévient la pop-up (mêmes rappels qu'avec Upcart).
  4. Croix sur un code promo dans « Réductions » : retire le code (comme Upcart).
  5. Minuteur du panier (français) : panier conservé 10 minutes, vidé ensuite s'il n'est pas passé au paiement.
*/
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const url = (path) => routesRoot.replace(/\/?$/, '/') + path;
const drawerEl = () => document.querySelector('cart-drawer');
const drawerSectionId = () => (drawerEl() ? drawerEl().dataset.sectionId : null);

/* Nombre d'articles affiché sur l'icône : tous les articles, cadeaux compris, comme avec Upcart sur pandrum.fr (23/10). */
const visibleCount = (cart) => (cart.items || []).reduce((n, i) => n + i.quantity, 0);

const newBundleId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

async function fetchCart() {
  const r = await fetch(url('cart.js'), { headers: { Accept: 'application/json' } });
  return r.json();
}

function toast(type, message) {
  document.dispatchEvent(new CustomEvent('toast:open', { detail: { type, message } }));
}

function announce(source, cart, sections) {
  document.dispatchEvent(new CartUpdatedEvent({}, { itemCount: visibleCount(cart), source, sections }));
}

/* ---------- 5. Minuteur du panier (version française, comme le bandeau Upcart de pandrum.fr) ----------
   Le panier est conservé 10 minutes à partir du moment où il contient un article ; le délai continue d'une page à l'autre.
   À la fin du délai, le panier est vidé, sauf si le client est passé au paiement (clic sur « Valider mon panier »).
   Actif seulement quand le panier porte data-pd-minuteur (bandeau minuteur affiché : boutique en français). */
const MINUTEUR_CLE = 'pd_panier_minuteur';
let minuteurMemoire = null;
let minuteurVidage = false;
let minuteurPret = false; /* vrai une fois le panier de cette page vérifié (même panier que le minuteur enregistré) */

function minuteurDuree() {
  const d = drawerEl();
  const s = d ? parseInt(d.dataset.pdMinuteur, 10) : 0;
  return s > 0 ? s * 1000 : 0;
}

function minuteurLire() {
  try {
    const v = JSON.parse(localStorage.getItem(MINUTEUR_CLE) || 'null');
    if (v) return v;
  } catch (e) {}
  return minuteurMemoire;
}

function minuteurEcrire(v) {
  minuteurMemoire = v;
  try {
    if (v) localStorage.setItem(MINUTEUR_CLE, JSON.stringify(v));
    else localStorage.removeItem(MINUTEUR_CLE);
  } catch (e) {}
}

function minuteurSuivre(cart) {
  if (!minuteurDuree() || !cart) return;
  minuteurPret = true;
  if (!cart.item_count) {
    minuteurEcrire(null);
    return;
  }
  const m = minuteurLire();
  if (!m || m.token !== cart.token) {
    minuteurEcrire({ token: cart.token, fin: Date.now() + minuteurDuree(), paiement: false });
  }
  minuteurAfficher();
}

async function minuteurVider() {
  if (minuteurVidage) return;
  minuteurVidage = true;
  try {
    const sectionId = drawerSectionId();
    const r = await fetch(url('cart/clear.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ sections: sectionId || undefined }),
    });
    const res = await r.json();
    minuteurEcrire(null);
    if (sectionId && res.sections) announce('pandrum-minuteur', res, res.sections);
    syncCartIcon(res);
  } catch (e) {
    /* rien : nouvel essai à la prochaine seconde */
  } finally {
    minuteurVidage = false;
  }
}

function minuteurAfficher() {
  if (!minuteurDuree() || !minuteurPret) return;
  const m = minuteurLire();
  if (!m) return;
  const actif = !m.paiement;
  const reste = actif ? Math.max(0, m.fin - Date.now()) : 0;
  const mm = String(Math.floor(reste / 60000)).padStart(2, '0');
  const ss = String(Math.floor((reste % 60000) / 1000)).padStart(2, '0');
  document.querySelectorAll('[data-pd-minuteur-texte]').forEach((el) => {
    el.textContent = mm + ':' + ss;
  });
  document.querySelectorAll('[data-pd-minuteur-actif]').forEach((el) => {
    el.hidden = !actif;
  });
  document.querySelectorAll('[data-pd-minuteur-repli]').forEach((el) => {
    el.hidden = actif;
  });
  if (actif && reste === 0) minuteurVider();
}

/* Passage au paiement : le panier n'est plus vidé. */
document.addEventListener(
  'click',
  (event) => {
    if (!event.target.closest('[name="checkout"], a[href*="/checkout"]')) return;
    const m = minuteurLire();
    if (m) minuteurEcrire({ ...m, paiement: true });
  },
  true,
);

setInterval(minuteurAfficher, 1000);
document.addEventListener(ThemeEvents.cartUpdated, () => setTimeout(minuteurAfficher, 0));

/* ---------- 1. Ajout au panier ---------- */
function formItems(form) {
  const fd = new FormData(form);
  const id = Number(fd.get('id'));
  const quantity = Math.max(1, parseInt(fd.get('quantity'), 10) || 1);
  const properties = {};
  for (const [k, v] of fd.entries()) {
    const m = k.match(/^properties\[(.+)\]$/);
    if (m && v !== '') properties[m[1]] = v;
  }
  return { id, quantity, properties };
}

async function handleAdd(form, button) {
  const main = formItems(form);
  if (!main.id) return;
  const items = [];
  const offre = window.PandrumOffre;
  if (offre && offre.form === form) {
    const bundleId = newBundleId();
    main.properties._pandrum_offre = bundleId;
    items.push(main, ...offre.items(bundleId, main.quantity));
  } else {
    items.push(main);
  }
  if (!Object.keys(main.properties).length) delete main.properties;

  const sectionId = drawerSectionId();
  if (button) button.classList.add('is-loading');
  try {
    const r = await fetch(url('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionId || undefined }),
    });
    const res = await r.json();
    if (!r.ok || res.status) {
      toast('error', res.description || res.message || 'Erreur');
      return;
    }
    if (!sectionId) {
      window.location.href = url('cart');
      return;
    }
    const cart = await fetchCart();
    announce('product-form', cart, res.sections);
    syncCartIcon(cart);
    minuteurSuivre(cart);
  } catch (e) {
    toast('error', String(e));
  } finally {
    if (button) button.classList.remove('is-loading');
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add\/?$/.test(action.split('?')[0])) return;
    const pf = form.closest('product-form');
    // Le composant product-form du thème, quand il est actif, gère lui-même l'ajout.
    if (pf && customElements.get('product-form')) return;
    if (!form.closest('.lowdesign-top')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.submitter || form.querySelector('[type="submit"]');
    handleAdd(form, button);
  },
  true,
);

/* ---------- 2. Cadeaux alignés sur leur handpan ---------- */
let syncing = false;
async function syncBundles() {
  if (syncing) return;
  syncing = true;
  try {
    const cart = await fetchCart();
    minuteurSuivre(cart);
    const mains = {};
    cart.items.forEach((i) => {
      const p = i.properties || {};
      if (p._pandrum_offre && !p._pandrum_cadeau) mains[p._pandrum_offre] = (mains[p._pandrum_offre] || 0) + i.quantity;
    });
    const updates = {};
    cart.items.forEach((i) => {
      const p = i.properties || {};
      if (p._pandrum_cadeau && p._pandrum_offre) {
        const want = mains[p._pandrum_offre] || 0;
        if (i.quantity !== want) updates[i.key] = want;
      }
    });
    if (!Object.keys(updates).length) {
      syncCartIcon(cart);
      return;
    }
    const sectionId = drawerSectionId();
    const r = await fetch(url('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sectionId || undefined }),
    });
    const res = await r.json();
    minuteurSuivre(res);
    if (document.body.classList.contains('template-cart') || /\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }
    if (sectionId && res.sections) announce('pandrum-sync', res, res.sections);
  } catch (e) {
    /* rien : le panier reste utilisable */
  } finally {
    syncing = false;
  }
}

function syncCartIcon(cart) {
  const count = visibleCount(cart);
  document.querySelectorAll('cart-icon [data-ref="cart-count"]').forEach((el) => {
    el.textContent = count;
  });
}

document.addEventListener(ThemeEvents.cartUpdated, (event) => {
  const source = event.detail && event.detail.data && event.detail.data.source;
  if (source === 'pandrum-sync' || source === 'product-form') {
    setTimeout(() => fetchCart().then(syncCartIcon).catch(() => {}), 150);
    return;
  }
  setTimeout(syncBundles, 50);
});

/* Le panier a pu changer ailleurs (autre onglet, page panier) : contrôle au chargement. */
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => syncBundles());
else syncBundles();

/* ---------- 3. Ouverture / fermeture ---------- */
function watchDrawer() {
  const d = drawerEl();
  if (!d || d.dataset.pdWatched) return;
  d.dataset.pdWatched = '1';
  let wasOpen = d.classList.contains('is-open');
  new MutationObserver(() => {
    const open = d.classList.contains('is-open');
    if (open === wasOpen) return;
    wasOpen = open;
    document.documentElement.classList.toggle('pd-panier-ouvert', open);
    try {
      if (open && typeof window.upcartOnCartOpened === 'function') window.upcartOnCartOpened();
      if (!open && typeof window.upcartOnCartClosed === 'function') window.upcartOnCartClosed();
    } catch (e) {}
  }).observe(d, { attributes: true, attributeFilter: ['class'] });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
else watchDrawer();

/* ---------- 4. Croix sur un code promo dans « Réductions » : retire ce code (comme Upcart) ---------- */
document.addEventListener('click', async (event) => {
  const b = event.target.closest('[data-pd-code-remove]');
  if (!b) return;
  event.preventDefault();
  const code = (b.dataset.pdCodeRemove || '').toLowerCase();
  b.disabled = true;
  try {
    const cart = await fetchCart();
    const keep = (cart.discount_codes || []).map((d) => d.code).filter((c) => c && c.toLowerCase() !== code);
    const sectionId = drawerSectionId();
    const r = await fetch(url('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: keep.join(','), sections: sectionId || undefined }),
    });
    const res = await r.json();
    if (sectionId && res.sections) announce('pandrum-code', res, res.sections);
  } catch (e) {
    b.disabled = false;
  }
});

/* ------------------------------------------------------------------
   6. Anciens liens Google (numéros de variante de l'ancienne boutique)
   Les fiches Google encore servies par l'ancienne source pointent vers
   ?variant=<ancien numéro>. Le thème remplace ce numéro inconnu par la
   variante par défaut : on relit donc l'adresse d'origine de la visite,
   puis on retrouve la bonne variante :
   - par le SKU (shopify_ZZ_<ancien produit>_<ancien numéro>) ;
   - sinon par la table des 29 vraies références.
   ------------------------------------------------------------------ */
(function pandrumAnciennesVariantes() {
  try {
    if (!/\/products\/[^/]+/.test(location.pathname)) return;
    const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    const origine = new URL(nav && nav.name ? nav.name : location.href);
    if (origine.pathname.replace(/\/$/, '') !== location.pathname.replace(/\/$/, '')) return;
    const ancien = origine.searchParams.get('variant');
    if (!ancien || !/^\d+$/.test(ancien)) return;
    const table = {
      '47998419566786': 59295040569676, '47998419599554': 59295040602444, '47998421270722': 59295050826060,
      '47998421303490': 59295050858828, '47998423236802': 59295057084748, '47998423400642': 59295057248588,
      '47998425202882': 59295063507276, '47998425235650': 59295063540044, '47998431396034': 59295083856204,
      '47998431428802': 59295083888972, '47998431461570': 59295083921740, '47998431494338': 59295083954508,
      '47998431527106': 59295083987276, '47998431559874': 59295084020044, '47998431592642': 59295084085580,
      '47998431625410': 59295084118348, '47998431658178': 59295084151116, '47998431690946': 59295084183884,
      '47998431723714': 59295084216652, '47998431756482': 59295084249420, '47998431789250': 59295084282188,
      '47998431822018': 59295084314956, '47998431854786': 59295084347724, '47998431887554': 59295084380492,
      '47998431920322': 59295086444876, '47998431953090': 59295086477644, '47998431985858': 59295086510412,
      '47998432018626': 59295086543180, '47998432051394': 59295086575948,
    };
    const chemin = location.pathname.replace(/\/$/, '');
    fetch(chemin + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p || !Array.isArray(p.variants) || p.variants.length < 2) return;
        if (p.variants.some((v) => String(v.id) === ancien)) return;
        let cible = null;
        const fin = '_' + ancien;
        for (const v of p.variants) {
          const sku = String(v.sku || '');
          if (sku.startsWith('shopify_ZZ_') && sku.endsWith(fin)) { cible = v.id; break; }
        }
        if (!cible && table[ancien] && p.variants.some((v) => v.id === table[ancien])) cible = table[ancien];
        if (!cible) return;
        const params = new URLSearchParams(origine.search);
        params.set('variant', String(cible));
        location.replace(chemin + '?' + params.toString() + location.hash);
      })
      .catch(() => {});
  } catch (e) {
    /* rien : la page reste telle quelle */
  }
})();
